import React, { useState } from "react";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer, withNulls } from "../hrForm";
import { EmptyState, LoadState, PrimaryButton, Tabs } from "../hrComponents";
import { cardStyle, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../hrUi";

// DOC-02 / DOC-06: which documents each employee must have, and the letter templates HR issues.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const yes = (v) => (v ? "Yes" : "—");

const typeFields = (creating) => [
  { key: "code", label: "Code", required: true, max: 32, disabled: !creating, hint: creating ? "Letters, digits and underscores, e.g. PASSPORT" : "The code cannot change" },
  { key: "name", label: "Name", required: true, max: 100 },
  { key: "is_mandatory", label: "Every employee must have it", type: "checkbox" },
  { key: "needs_verification", label: "HR verifies it after upload", type: "checkbox" },
  { key: "has_expiry", label: "Has an expiry date", type: "checkbox" },
  { key: "visible_to_employee", label: "The employee can see it", type: "checkbox" },
];

function DocumentTypes() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listDocumentTypes(true), []);
  const [edit, setEdit] = useState(undefined);
  const rows = data || [];
  const keys = typeFields(false).map((f) => f.key).filter((k) => k !== "code");
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add document type</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No document types" text="Saving your HR settings adds the usual ones for your country." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Document", "Required", "Verified by HR", "Expires", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td style={tdStyle}>{t.name}</td>
                    <td style={tdStyle}>{yes(t.is_mandatory)}</td>
                    <td style={tdStyle}>{yes(t.needs_verification)}</td>
                    <td style={tdStyle}>{yes(t.has_expiry)}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(t.status)}>{statusLabel(t.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit(t)}>
                        Edit
                      </button>{" "}
                      <button type="button" style={linkBtn} onClick={async () => { await hrmsService.saveDocumentType(t.id, { status: t.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }); reload(); }}>
                        {t.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit document type" : "Add document type"}
        fields={typeFields(!edit)}
        initial={edit || { visible_to_employee: true }}
        onHide={() => setEdit(undefined)}
        onSave={(clean, raw) => (edit ? hrmsService.saveDocumentType(edit.id, withNulls(raw, keys)) : hrmsService.saveDocumentType(null, clean))}
        onDone={reload}
      />
    </>
  );
}

const MERGE_FIELDS = ["employee_name", "employee_code", "designation", "department", "location", "date_of_joining", "confirmation_date", "last_working_day", "company_name", "letter_no", "today"];

function LetterTemplates() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listLetterTemplates({ body: 1, all: 1 }), []);
  const [edit, setEdit] = useState(undefined);
  const rows = data || [];
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add letter template</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No letter templates" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["Code", "Name", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td style={tdStyle}>{t.code}</td>
                    <td style={tdStyle}>{t.name}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(t.status)}>{statusLabel(t.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit(t)}>
                        Edit
                      </button>{" "}
                      <button type="button" style={linkBtn} onClick={async () => { await hrmsService.saveLetterTemplate(t.id, { status: t.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }); reload(); }}>
                        {t.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit letter template" : "Add letter template"}
        width={640}
        fields={[
          { key: "code", label: "Code", required: true, max: 32, disabled: !!edit },
          { key: "name", label: "Name", required: true, max: 100 },
          { key: "body_html", label: "Letter (HTML)", type: "textarea", rows: 14, required: true, max: 60000, hint: `Merge fields: ${MERGE_FIELDS.map((f) => `{{${f}}}`).join(" ")}` },
        ]}
        initial={edit || {}}
        onHide={() => setEdit(undefined)}
        onSave={(clean) => (edit ? hrmsService.saveLetterTemplate(edit.id, { name: clean.name, body_html: clean.body_html }) : hrmsService.saveLetterTemplate(null, clean))}
        onDone={reload}
      />
    </>
  );
}

export default function DocumentSetupTab() {
  const [tab, setTab] = useState("types");
  return (
    <>
      <Tabs tabs={[{ key: "types", label: "Document types" }, { key: "letters", label: "Letter templates" }]} active={tab} onChange={setTab} />
      {tab === "types" ? <DocumentTypes /> : <LetterTemplates />}
    </>
  );
}
